'use client'

import React, { createContext, useContext, useState, useEffect } from 'react'
import { toast } from 'sonner'

export interface User {
  id: string
  email: string
  name: string
  role: 'buyer' | 'procurement' | 'engineer' | 'admin'
  company: {
    id: string
    name: string
    industry: string
    size: string
  }
  preferences: {
    currency: string
    language: string
    notifications: boolean
  }
  createdAt: Date
  lastLoginAt: Date
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<boolean>
  register: (userData: RegisterData) => Promise<boolean>
  logout: () => void
  updateProfile: (updates: Partial<User>) => Promise<boolean>
}

interface RegisterData {
  email: string
  password: string
  name: string
  companyName: string
  industry: string
  role: User['role']
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Mock user data for demo
  const mockUser: User = {
    id: 'user_123',
    email: 'john.doe@techcorp.com',
    name: 'John Doe',
    role: 'procurement',
    company: {
      id: 'company_456',
      name: 'TechCorp Industries',
      industry: 'Electronics Manufacturing',
      size: '1000-5000 employees'
    },
    preferences: {
      currency: 'USD',
      language: 'en',
      notifications: true
    },
    createdAt: new Date('2024-01-15'),
    lastLoginAt: new Date()
  }

  useEffect(() => {
    // Simulate checking for existing session
    const checkAuth = async () => {
      try {
        const savedUser = localStorage.getItem('prosoft_user')
        if (savedUser) {
          setUser(JSON.parse(savedUser))
        }
      } catch (error) {
        console.error('Auth check failed:', error)
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [])

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true)
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Mock validation
      if (email === 'demo@prosoft.com' && password === 'demo123') {
        const userWithUpdatedLogin = {
          ...mockUser,
          email,
          lastLoginAt: new Date()
        }
        setUser(userWithUpdatedLogin)
        localStorage.setItem('prosoft_user', JSON.stringify(userWithUpdatedLogin))
        toast.success(`Welcome back, ${userWithUpdatedLogin.name}!`)
        return true
      } else {
        toast.error('Invalid credentials. Use demo@prosoft.com / demo123')
        return false
      }
    } catch (error) {
      toast.error('Login failed. Please try again.')
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const register = async (userData: RegisterData): Promise<boolean> => {
    setIsLoading(true)
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500))
      
      const newUser: User = {
        id: `user_${Date.now()}`,
        email: userData.email,
        name: userData.name,
        role: userData.role,
        company: {
          id: `company_${Date.now()}`,
          name: userData.companyName,
          industry: userData.industry,
          size: 'Unknown'
        },
        preferences: {
          currency: 'USD',
          language: 'en',
          notifications: true
        },
        createdAt: new Date(),
        lastLoginAt: new Date()
      }
      
      setUser(newUser)
      localStorage.setItem('prosoft_user', JSON.stringify(newUser))
      toast.success(`Account created successfully! Welcome, ${newUser.name}!`)
      return true
    } catch (error) {
      toast.error('Registration failed. Please try again.')
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const logout = () => {
    setUser(null)
    localStorage.removeItem('prosoft_user')
    toast.success('Logged out successfully')
  }

  const updateProfile = async (updates: Partial<User>): Promise<boolean> => {
    if (!user) return false
    
    try {
      const updatedUser = { ...user, ...updates }
      setUser(updatedUser)
      localStorage.setItem('prosoft_user', JSON.stringify(updatedUser))
      toast.success('Profile updated successfully')
      return true
    } catch (error) {
      toast.error('Failed to update profile')
      return false
    }
  }

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    register,
    logout,
    updateProfile
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
